/**
 * Configuración de tarifas de envío de la tienda física.
 * Matriz paquete × destino + ajustes por zona (envío gratis, plazos).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, Save, Truck, Info, Package } from 'lucide-react';
import { cn } from '@/lib/utils';
import { esCount } from '@/lib/es-count';
import { formatDateTime, formatEurFromCents, formatNumber } from '@/lib/format-es';
import { notify } from '@/lib/notify';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import {
    useStoreShippingRatesList,
    useUpdateStoreShippingRate,
    useStoreShippingPackageRatesList,
    useUpdateStoreShippingPackagePrices,
    useUpsertStoreShippingPackagePrices,
    groupPackageRates,
} from './hooks/useStoreShippingRates';
import type {
    StoreShippingPackagePriceFormValues,
    StoreShippingRateFormValues,
    StoreShippingRateRow,
} from './types';

const LEGACY_ZONE_CODES = new Set(['baleares']);

function packageCellFormKey(packageTypeCode: string, zoneCode: string): string {
    return `new:${packageTypeCode}:${zoneCode}`;
}

const ZONE_HINTS: Record<string, string> = {
    peninsula: 'Península ibérica española (excepto Baleares, Canarias, Ceuta y Melilla)',
    baleares: 'Illes Balears (legacy) — sustituido por Mallorca / Islas Menores',
    baleares_mallorca: 'Mallorca — CP 07xxx (excepto 077 y 078)',
    baleares_menores: 'Menorca (077), Ibiza y Formentera (078)',
    canarias: 'Canarias, Ceuta y Melilla — CP 35xx, 38xx, 51xx o 52xx',
    portugal: 'Portugal peninsular — CP 4 dígitos (no Azores ni Madeira)',
    france: 'Francia metropolitana — CP 5 dígitos',
    international: 'Resto del mundo — contacto manual (pedidos@aipeces.com)',
};

const ZONE_COLUMN_LABELS: Record<string, string> = {
    peninsula: 'Península 24h',
    baleares: 'Baleares (legacy)',
    baleares_mallorca: 'Baleares (Mallorca)',
    baleares_menores: 'Baleares (Menores)',
    canarias: 'Ceuta-Melilla-Canarias',
    portugal: 'Portugal',
    france: 'Francia',
    international: 'Internacional',
};

function centsToInputEur(cents: number | string): string {
    const n = typeof cents === 'number' ? cents : Number.parseInt(String(cents), 10);
    if (!Number.isFinite(n)) return '';
    const eur = n / 100;
    return Number.isInteger(eur) ? String(eur) : eur.toFixed(2).replace('.', ',');
}

function parseInputEur(raw: string): number | null {
    const t = raw.trim().replace(',', '.');
    if (!t) return null;
    const n = Number.parseFloat(t);
    if (!Number.isFinite(n) || n < 0) return null;
    return Math.round(n * 100);
}

function formatBillableWeight(kg: number | string | null, note: string | null): string {
    if (kg == null || kg === '') return '—';
    const n = typeof kg === 'number' ? kg : Number.parseFloat(String(kg));
    if (!Number.isFinite(n)) return '—';
    const base = `${formatNumber(n)} kg`;
    return note ? `${base} (${note})` : base;
}

function rowToForm(row: StoreShippingRateRow): StoreShippingRateFormValues {
    const hasFree = row.free_threshold_cents != null && row.free_threshold_cents > 0;
    return {
        priceEur: centsToInputEur(row.price_cents),
        freeThresholdEur: hasFree ? centsToInputEur(row.free_threshold_cents!) : '',
        noFreeShipping: !hasFree,
    };
}

function isZoneFormDirty(row: StoreShippingRateRow, form: StoreShippingRateFormValues): boolean {
    if (form.noFreeShipping) {
        return row.free_threshold_cents != null;
    }
    const thr = parseInputEur(form.freeThresholdEur);
    if (thr === null) return true;
    return thr !== row.free_threshold_cents;
}

export function StoreShippingRatesPage() {
    const {
        data: rates,
        isLoading: ratesLoading,
        isError: ratesError,
        error: ratesErr,
        refetch: refetchRates,
        isFetching: ratesFetching,
    } = useStoreShippingRatesList();
    const {
        data: packageRows,
        isLoading: packageLoading,
        isError: packageError,
        error: packageErr,
        refetch: refetchPackage,
        isFetching: packageFetching,
    } = useStoreShippingPackageRatesList();

    const updateRate = useUpdateStoreShippingRate();
    const updatePackagePrices = useUpdateStoreShippingPackagePrices();
    const upsertPackagePrices = useUpsertStoreShippingPackagePrices();

    const [zoneForms, setZoneForms] = useState<Record<string, StoreShippingRateFormValues>>({});
    const [packageForms, setPackageForms] = useState<Record<string, StoreShippingPackagePriceFormValues>>({});

    const packageGroups = useMemo(
        () => groupPackageRates(packageRows ?? []),
        [packageRows]
    );

    const activeRates = useMemo(
        () => (rates ?? []).filter((r) => r.is_active && !LEGACY_ZONE_CODES.has(r.code)),
        [rates]
    );

    const zoneCodes = useMemo(() => {
        return activeRates
            .slice()
            .sort((a, b) => a.sort_order - b.sort_order)
            .map((r) => r.code);
    }, [activeRates]);

    const ratesSnapshotKey = useMemo(
        () =>
            (rates ?? [])
                .map(
                    (r) =>
                        `${r.id}:${r.price_cents}:${r.free_threshold_cents ?? ''}:${r.updated_at}`
                )
                .join('|'),
        [rates]
    );

    const packageSnapshotKey = useMemo(
        () =>
            (packageRows ?? [])
                .map((r) => `${r.id}:${r.price_cents}:${r.updated_at}`)
                .join('|'),
        [packageRows]
    );

    useEffect(() => {
        if (!rates?.length) return;
        setZoneForms(() => {
            const next: Record<string, StoreShippingRateFormValues> = {};
            for (const r of rates) next[r.id] = rowToForm(r);
            return next;
        });
    }, [ratesSnapshotKey, rates]);

    useEffect(() => {
        if (!packageRows?.length) return;
        setPackageForms(() => {
            const next: Record<string, StoreShippingPackagePriceFormValues> = {};
            for (const r of packageRows) {
                next[r.id] = { priceEur: centsToInputEur(r.price_cents) };
            }
            return next;
        });
    }, [packageSnapshotKey, packageRows]);

    const dirtyZoneIds = useMemo(() => {
        if (!rates) return [];
        return rates
            .filter((r) => {
                const form = zoneForms[r.id];
                return form ? isZoneFormDirty(r, form) : false;
            })
            .map((r) => r.id);
    }, [rates, zoneForms]);

    const dirtyPackageIds = useMemo(() => {
        if (!packageRows) return [];
        return packageRows
            .filter((r) => {
                const form = packageForms[r.id];
                if (!form) return false;
                const cents = parseInputEur(form.priceEur);
                const stored = Number.parseInt(String(r.price_cents), 10);
                return cents === null || cents !== stored;
            })
            .map((r) => r.id);
    }, [packageRows, packageForms]);

    const dirtyNewPackageKeys = useMemo(() => {
        const keys: string[] = [];
        for (const group of packageGroups) {
            for (const zoneCode of zoneCodes) {
                if (group.pricesByZone[zoneCode]) continue;
                const key = packageCellFormKey(group.code, zoneCode);
                const form = packageForms[key];
                if (form?.priceEur.trim()) keys.push(key);
            }
        }
        return keys;
    }, [packageGroups, zoneCodes, packageForms]);

    const totalDirty = dirtyZoneIds.length + dirtyPackageIds.length + dirtyNewPackageKeys.length;
    const isSaving = updateRate.isPending || updatePackagePrices.isPending || upsertPackagePrices.isPending;
    const isLoading = ratesLoading || packageLoading;
    const isError = ratesError || packageError;
    const isFetching = ratesFetching || packageFetching;

    const setZoneField = useCallback(
        (id: string, patch: Partial<StoreShippingRateFormValues>) => {
            setZoneForms((prev) => ({
                ...prev,
                [id]: { ...prev[id], ...patch },
            }));
        },
        []
    );

    const setPackageField = useCallback((id: string, priceEur: string) => {
        setPackageForms((prev) => ({
            ...prev,
            [id]: { priceEur },
        }));
    }, []);

    const validateZoneForm = (form: StoreShippingRateFormValues): string | null => {
        if (!form.noFreeShipping) {
            const thr = parseInputEur(form.freeThresholdEur);
            if (thr === null || thr <= 0) return 'Umbral de envío gratis no válido (o marca "Sin envío gratis")';
        }
        return null;
    };

    const buildZoneUpdatePayload = (row: StoreShippingRateRow, form: StoreShippingRateFormValues) => {
        if (form.noFreeShipping) {
            return {
                rateId: row.id,
                priceCents: row.price_cents,
                freeThresholdCents: null,
                clearFreeThreshold: true,
                deliveryDaysMin: row.delivery_days_min,
                deliveryDaysMax: row.delivery_days_max,
            };
        }
        return {
            rateId: row.id,
            priceCents: row.price_cents,
            freeThresholdCents: parseInputEur(form.freeThresholdEur),
            clearFreeThreshold: false,
            deliveryDaysMin: row.delivery_days_min,
            deliveryDaysMax: row.delivery_days_max,
        };
    };

    const handleSaveAll = async () => {
        if (!rates?.length) return;
        if (totalDirty === 0) {
            notify({ type: 'ok', text: 'No hay cambios pendientes.' });
            return;
        }

        for (const row of rates.filter((r) => dirtyZoneIds.includes(r.id))) {
            const form = zoneForms[row.id];
            const err = validateZoneForm(form);
            if (err) {
                notify({ type: 'err', text: `${row.label}: ${err}` });
                return;
            }
        }

        for (const row of packageRows?.filter((r) => dirtyPackageIds.includes(r.id)) ?? []) {
            const form = packageForms[row.id];
            const cents = parseInputEur(form.priceEur);
            if (cents === null) {
                notify({
                    type: 'err',
                    text: `${row.package_type_label} · ${row.zone_label}: precio no válido`,
                });
                return;
            }
        }

        for (const key of dirtyNewPackageKeys) {
            const form = packageForms[key];
            const cents = parseInputEur(form?.priceEur ?? '');
            if (cents === null) {
                const [, pkgCode, zoneCode] = key.split(':');
                notify({
                    type: 'err',
                    text: `${pkgCode} · ${ZONE_COLUMN_LABELS[zoneCode] ?? zoneCode}: precio no válido`,
                });
                return;
            }
        }

        try {
            if (dirtyPackageIds.length > 0) {
                const updates = dirtyPackageIds.map((id) => ({
                    id,
                    priceCents: parseInputEur(packageForms[id].priceEur)!,
                }));
                await updatePackagePrices.mutateAsync(updates);
            }

            if (dirtyNewPackageKeys.length > 0) {
                const upserts = dirtyNewPackageKeys.map((key) => {
                    const [, packageTypeCode, zoneCode] = key.split(':');
                    return {
                        packageTypeCode,
                        zoneCode,
                        priceCents: parseInputEur(packageForms[key].priceEur)!,
                    };
                });
                await upsertPackagePrices.mutateAsync(upserts);
            }

            for (const row of rates.filter((r) => dirtyZoneIds.includes(r.id))) {
                await updateRate.mutateAsync(buildZoneUpdatePayload(row, zoneForms[row.id]));
            }

            notify({
                type: 'ok',
                text: `Guardado: ${esCount(totalDirty, 'cambio')}. Los nuevos pedidos usarán estos valores.`,
            });
        } catch (e) {
            notify({
                type: 'err',
                text: e instanceof Error ? e.message : 'Error al guardar',
            });
        }
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-16 text-muted-foreground">
                <Loader2 className="h-6 w-6 animate-spin mr-2" />
                Cargando tarifas…
            </div>
        );
    }

    if (isError) {
        const errMsg =
            (ratesErr instanceof Error ? ratesErr.message : null) ??
            (packageErr instanceof Error ? packageErr.message : null) ??
            'desconocido';
        return (
            <div className="rounded-xl border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">
                Error al cargar tarifas: {errMsg}
            </div>
        );
    }

    return (
        <div className="space-y-8">
            <div className="rounded-xl border border-border bg-card p-4 flex gap-3 text-sm text-muted-foreground">
                <Info className="h-5 w-5 shrink-0 text-primary" />
                <div>
                    <p>
                        El precio de envío depende del <strong className="text-foreground">tipo de paquete</strong>{' '}
                        (peso o cañas) y del <strong className="text-foreground">destino</strong> según código postal.
                        Los pedidos ya pagados conservan su snapshot.
                    </p>
                    <p className="mt-1">
                        La app refresca el catálogo en ~1 minuto tras guardar.
                    </p>
                </div>
            </div>

            {/* Matriz paquete × destino */}
            <section className="space-y-4">
                <div className="flex items-center gap-2">
                    <Package className="h-5 w-5 text-primary" />
                    <h2 className="text-base font-semibold text-foreground">Tarifas por tipo de paquete</h2>
                </div>

                <div className="rounded-xl border border-border bg-card overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-border bg-muted/40">
                                    <th className="text-left font-medium text-foreground px-4 py-3 min-w-[160px]">
                                        Tipo de envío
                                    </th>
                                    <th className="text-left font-medium text-foreground px-4 py-3 min-w-[140px]">
                                        Medidas
                                    </th>
                                    <th className="text-left font-medium text-foreground px-4 py-3 min-w-[140px]">
                                        Peso facturable*
                                    </th>
                                    {zoneCodes.map((code) => (
                                        <th
                                            key={code}
                                            className="text-left font-medium text-foreground px-4 py-3 min-w-[120px]"
                                        >
                                            {ZONE_COLUMN_LABELS[code] ?? code}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {packageGroups.map((group) => (
                                    <tr key={group.code} className="border-b border-border last:border-0">
                                        <td className="px-4 py-3 align-top font-medium text-foreground">
                                            {group.label}
                                        </td>
                                        <td className="px-4 py-3 align-top text-muted-foreground">
                                            {group.dimensions ?? '—'}
                                        </td>
                                        <td className="px-4 py-3 align-top text-muted-foreground">
                                            {formatBillableWeight(group.billableWeightKg, group.billableWeightNote)}
                                        </td>
                                        {zoneCodes.map((zoneCode) => {
                                            const cell = group.pricesByZone[zoneCode];
                                            const newKey = packageCellFormKey(group.code, zoneCode);
                                            const formKey = cell?.id ?? newKey;
                                            const form = packageForms[formKey] ?? {
                                                priceEur: cell ? centsToInputEur(cell.priceCents) : '',
                                            };
                                            const isDirty =
                                                (cell && dirtyPackageIds.includes(cell.id)) ||
                                                dirtyNewPackageKeys.includes(newKey);
                                            const isNoShipping = !cell && !form.priceEur.trim();

                                            return (
                                                <td key={zoneCode} className="px-4 py-3 align-top">
                                                    <div className="space-y-1">
                                                        <Input
                                                            type="text"
                                                            inputMode="decimal"
                                                            placeholder="Sin envío"
                                                            value={form.priceEur}
                                                            onChange={(e) =>
                                                                setPackageField(formKey, e.target.value)
                                                            }
                                                            className={cn(
                                                                'h-9 w-24',
                                                                isDirty && 'border-primary/50',
                                                                isNoShipping && 'text-muted-foreground placeholder:text-muted-foreground/80'
                                                            )}
                                                            aria-label={`Precio ${group.label} · ${ZONE_COLUMN_LABELS[zoneCode] ?? zoneCode}`}
                                                        />
                                                        <span className="text-xs text-muted-foreground">€</span>
                                                        {isDirty && (
                                                            <span className="block text-xs text-primary">sin guardar</span>
                                                        )}
                                                    </div>
                                                </td>
                                            );
                                        })}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <p className="px-4 py-2 text-xs text-muted-foreground border-t border-border">
                        * Peso facturable según transportista. Cañas: peso volumétrico (1,30 m × 0,10 m × 0,10 m → 3 kg).
                        Cañas &gt; 1,30 m solo disponibles en Península ES y Portugal.
                    </p>
                </div>
            </section>

            {/* Configuración por zona (envío gratis merch) */}
            <section className="space-y-4">
                <div className="flex items-center gap-2">
                    <Truck className="h-5 w-5 text-primary" />
                    <h2 className="text-base font-semibold text-foreground">Configuración por zona</h2>
                </div>
                <p className="text-xs text-muted-foreground -mt-2">
                    Los plazos de entrega de cada distribuidor se configuran en su ficha, pestaña Condiciones.
                </p>

                <div className="space-y-4">
                    {activeRates.map((row) => {
                        const form = zoneForms[row.id] ?? rowToForm(row);
                        const hint = ZONE_HINTS[row.code] ?? row.label;
                        const isDirty = dirtyZoneIds.includes(row.id);

                        return (
                            <div
                                key={row.id}
                                className={cn(
                                    'rounded-xl border bg-card p-5 space-y-4 transition-colors',
                                    isDirty ? 'border-primary/50' : 'border-border'
                                )}
                            >
                                <div className="flex items-start justify-between gap-4">
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <h3 className="text-sm font-semibold text-foreground">{row.label}</h3>
                                            <span className="text-xs font-mono text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                                                {row.code}
                                            </span>
                                            {isDirty && (
                                                <span className="text-xs text-primary font-medium">sin guardar</span>
                                            )}
                                        </div>
                                        <p className="text-xs text-muted-foreground mt-1">{hint}</p>
                                        {!row.is_active && (
                                            <p className="text-xs text-destructive mt-0.5">(inactiva)</p>
                                        )}
                                    </div>
                                    {row.updated_at && (
                                        <p className="text-xs text-muted-foreground whitespace-nowrap">
                                            Actualizado {formatDateTime(row.updated_at)}
                                        </p>
                                    )}
                                </div>

                                <div className="grid gap-4 sm:grid-cols-2 max-w-2xl">
                                    <div className="space-y-2">
                                        <Label>Envío merch (paquete 1 kg)</Label>
                                        <p className="text-sm font-medium text-foreground">
                                            {formatEurFromCents(
                                                packageGroups
                                                    .find((g) => g.code === 'package_1kg')
                                                    ?.pricesByZone[row.code]?.priceCents ?? row.price_cents
                                            )}
                                        </p>
                                        <p className="text-xs text-muted-foreground">
                                            Lo cobra el checkout. Se edita en la matriz de arriba, no aquí.
                                        </p>
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor={`free-${row.id}`}>Envío gratis desde subtotal (€)</Label>
                                        <Input
                                            id={`free-${row.id}`}
                                            type="text"
                                            inputMode="decimal"
                                            placeholder="50"
                                            disabled={form.noFreeShipping}
                                            value={form.freeThresholdEur}
                                            onChange={(e) =>
                                                setZoneField(row.id, { freeThresholdEur: e.target.value })
                                            }
                                        />
                                        <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={form.noFreeShipping}
                                                onChange={(e) =>
                                                    setZoneField(row.id, {
                                                        noFreeShipping: e.target.checked,
                                                        ...(e.target.checked ? { freeThresholdEur: '' } : {}),
                                                    })
                                                }
                                                className="rounded border-input"
                                            />
                                            Sin envío gratis (siempre cobrar envío)
                                        </label>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </section>

            <div className="flex flex-wrap items-center gap-3">
                <Button
                    onClick={() => void handleSaveAll()}
                    disabled={isSaving || totalDirty === 0}
                >
                    {isSaving ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                        <Save className="h-4 w-4" />
                    )}
                    Guardar cambios
                    {totalDirty > 0 && ` (${totalDirty})`}
                </Button>
                <button
                    type="button"
                    onClick={() => {
                        void refetchRates();
                        void refetchPackage();
                    }}
                    disabled={isFetching}
                    className="text-sm text-muted-foreground hover:text-foreground disabled:opacity-50"
                >
                    Recargar
                </button>
            </div>
        </div>
    );
}
